import {
  FETCH_TBT_SETTINGS_REQUEST,
  FETCH_TBT_SETTINGS_SUCCESS,
  FETCH_TBT_SETTINGS_FAILURE,
  FETCH_TBT_RUNS_REQUEST,
  FETCH_TBT_RUNS_SUCCESS,
  FETCH_TBT_RUNS_FAILURE,
  FETCH_RUN_DETAILS_REQUEST,
  FETCH_RUN_DETAILS_SUCCESS,
  FETCH_RUN_DETAILS_FAILURE,
  FETCH_TBT_DOCUMENTS_REQUEST,
  FETCH_TBT_DOCUMENTS_SUCCESS,
  FETCH_TBT_DOCUMENTS_FAILURE,
  UPLOAD_TBT_DOCUMENT_REQUEST,
  UPLOAD_TBT_DOCUMENT_SUCCESS,
  UPLOAD_TBT_DOCUMENT_FAILURE,
  DELETE_TBT_DOCUMENT_REQUEST,
  DELETE_TBT_DOCUMENT_SUCCESS,
  DELETE_TBT_DOCUMENT_FAILURE,
  TOGGLE_TBT_DOCUMENT_REQUEST,
  TOGGLE_TBT_DOCUMENT_SUCCESS,
  TOGGLE_TBT_DOCUMENT_FAILURE,
  FETCH_TBT_STATISTICS_REQUEST,
  FETCH_TBT_STATISTICS_SUCCESS,
  FETCH_TBT_STATISTICS_FAILURE,
} from '../reducer'

import {
  fetchTBTSettings,
  fetchTBTRuns,
  fetchRunDetails,
  triggerManualTBTRun,
  fetchTBTDocuments,
  uploadTBTDocument,
  deleteTBTDocument,
  toggleTBTDocumentActive,
  fetchTBTStatistics,
} from '../Services'

export const handleFetchSettings = () => async (dispatch) => {
  dispatch({ type: FETCH_TBT_SETTINGS_REQUEST })

  try {
    const response = await fetchTBTSettings()

    // Transform settings array into both values and definitions for proper display
    const settingsArray = response.data.rows || []
    const settingsObject = {}
    const settingsDefinitions = {}

    settingsArray.forEach(setting => {
      // Store the raw value
      settingsObject[setting.token] = setting.value
      
      // Store the setting definition for display formatting
      settingsDefinitions[setting.token] = {
        field_type_id: setting.field_type_id,
        processor_json: setting.processor_json,
        title: setting.title,
        help_text: setting.help_text
      }
    })

    dispatch({
      type: FETCH_TBT_SETTINGS_SUCCESS,
      payload: {
        values: settingsObject,
        definitions: settingsDefinitions
      }
    })
  } catch (error) {
    dispatch({
      type: FETCH_TBT_SETTINGS_FAILURE,
      payload: error.message
    })
  }
}

export const handleFetchRuns = (params = {}) => async (dispatch) => {
  dispatch({ type: FETCH_TBT_RUNS_REQUEST })

  try {
    const response = await fetchTBTRuns(params)

    // Transform the data to match what the frontend component expects
    const runsData = response.data.rows || response.data.data || []
    const transformedRuns = runsData.map(run => ({
      ...run,
      // Map API field names to what the component expects
      total_recipients: run.total_sent,
      signed_count: run.total_signed,
      viewed_count: run.total_viewed,
      documents_count: run.documents_used ? run.documents_used.length : 0,
      started_at: run.run_at, // For duration calculation
    }))

    // Flatten pagination structure to match reducer expectations
    const pagination = response.data.pagination || {}
    const transformedData = {
      data: transformedRuns,
      // Extract pagination properties from nested structure
      current_page: pagination.current_page || 1,
      total: pagination.total || 0,
      per_page: pagination.per_page || 15,
      last_page: pagination.last_page || 1,
      from: pagination.from || 0,
      to: pagination.to || 0,
    }

    dispatch({
      type: FETCH_TBT_RUNS_SUCCESS,
      payload: transformedData
    })
  } catch (error) {
    dispatch({
      type: FETCH_TBT_RUNS_FAILURE,
      payload: error.message
    })
  }
}

export const handleFetchRunDetails = (runId) => async (dispatch) => {
  dispatch({ type: FETCH_RUN_DETAILS_REQUEST })

  try {
    const response = await fetchRunDetails(runId)
    
    // Use the data directly from backend - it already includes the properly formatted recipients
    const runData = response.data
    const transformedRunData = {
      ...runData,
      // Use recipients array directly from backend (includes email details)
      recipients: runData.recipients || [],
      // Transform documents if available (backend returns documentsUsed, not documents_used)
      documents: runData.documents_used || runData.documentsUsed || [],
    }
    
    dispatch({
      type: FETCH_RUN_DETAILS_SUCCESS,
      payload: transformedRunData
    })
  } catch (error) {
    dispatch({
      type: FETCH_RUN_DETAILS_FAILURE,
      payload: error.message
    })
  }
}

export const handleTableChange = (pagination, filters, sorter) => async (dispatch, getState) => {
  const params = {
    page: pagination.current,
    per_page: pagination.pageSize,
  }

  // Add sorting
  if (sorter.field) {
    params.sort_by = sorter.field
    params.sort_order = sorter.order === 'ascend' ? 'asc' : 'desc'
  }

  // Add filters
  Object.keys(filters).forEach(key => {
    if (filters[key] && filters[key].length > 0) {
      params[key] = filters[key]
    }
  })

  dispatch(handleFetchRuns(params))
}

export const handleManualTBTRun = () => async (dispatch) => {
  dispatch({ type: 'MANUAL_TBT_RUN_REQUEST' })

  try {
    const response = await triggerManualTBTRun()

    dispatch({
      type: 'MANUAL_TBT_RUN_SUCCESS',
      payload: response.data
    })

    // Refresh runs list after successful manual run
    dispatch(handleFetchRuns({ page: 1, per_page: 20 }))

    return response.data
  } catch (error) {
    dispatch({
      type: 'MANUAL_TBT_RUN_FAILURE',
      payload: (error.response && error.response.data && error.response.data.message) || 'Failed to run TBT distribution'
    })
    throw error
  }
}

// Document Management Handlers
export const handleFetchDocuments = (params = {}) => async (dispatch) => {
  dispatch({ type: FETCH_TBT_DOCUMENTS_REQUEST })

  try {
    const response = await fetchTBTDocuments(params)
    dispatch({
      type: FETCH_TBT_DOCUMENTS_SUCCESS,
      payload: response.data
    })
  } catch (error) {
    dispatch({
      type: FETCH_TBT_DOCUMENTS_FAILURE,
      payload: error.message
    })
  }
}

export const handleUploadDocument = (formData) => async (dispatch) => {
  dispatch({ type: UPLOAD_TBT_DOCUMENT_REQUEST })

  try {
    const response = await uploadTBTDocument(formData)
    dispatch({
      type: UPLOAD_TBT_DOCUMENT_SUCCESS,
      payload: response.data
    })
    return response.data
  } catch (error) {
    dispatch({
      type: UPLOAD_TBT_DOCUMENT_FAILURE,
      payload: (error.response && error.response.data && error.response.data.message) || 'Failed to upload document'
    })
    throw error
  }
}

export const handleDeleteDocument = (id) => async (dispatch) => {
  dispatch({ type: DELETE_TBT_DOCUMENT_REQUEST })

  try {
    await deleteTBTDocument(id)
    dispatch({
      type: DELETE_TBT_DOCUMENT_SUCCESS,
      payload: { id }
    })
    return { id }
  } catch (error) {
    dispatch({
      type: DELETE_TBT_DOCUMENT_FAILURE,
      payload: (error.response && error.response.data && error.response.data.message) || 'Failed to delete document'
    })
    throw error
  }
}

export const handleToggleDocumentActive = (id) => async (dispatch) => {
  dispatch({ type: TOGGLE_TBT_DOCUMENT_REQUEST })

  try {
    const response = await toggleTBTDocumentActive(id)
    dispatch({
      type: TOGGLE_TBT_DOCUMENT_SUCCESS,
      payload: { id }
    })
    return response.data
  } catch (error) {
    dispatch({
      type: TOGGLE_TBT_DOCUMENT_FAILURE,
      payload: (error.response && error.response.data && error.response.data.message) || 'Failed to update document status'
    })
    throw error
  }
}

export const handleFetchStatistics = () => async (dispatch) => {
  dispatch({ type: FETCH_TBT_STATISTICS_REQUEST })

  try {
    const response = await fetchTBTStatistics()
    dispatch({
      type: FETCH_TBT_STATISTICS_SUCCESS,
      payload: response.data
    })
  } catch (error) {
    dispatch({
      type: FETCH_TBT_STATISTICS_FAILURE,
      payload: error.message
    })
  }
}
